import fs from "node:fs";
import path from "node:path";
import { createRequire } from "node:module";
import { execFileSync } from "node:child_process";
import {
	createCipheriv,
	createDecipheriv,
	hkdfSync,
	randomBytes,
} from "node:crypto";
import { configDir } from "./config";
import { isNpx } from "../lib/npx";
import { buildTarget } from "../lib/build-info";

// Credential storage: OS keystore by default; a machine-bound encrypted file on macOS+npx or when the keystore is unavailable.

const SERVICE = "whop-cli";

/** Hardcoded pepper mixed into every derived key (defense in depth, not a secret). */
const KEY_PEPPER = "whop-cli/secret-store/v3";

type KeyringModule = typeof import("@napi-rs/keyring");

let keyringModule: KeyringModule | null | undefined;

function loadKeyring(): KeyringModule | null {
	if (keyringModule === undefined) {
		try {
			const require = createRequire(import.meta.url);
			keyringModule = require("@napi-rs/keyring") as KeyringModule;
		} catch {
			keyringModule = null;
		}
	}
	return keyringModule;
}

interface SecretStore {
	/** Returns false when the store is unavailable, so the next candidate is tried. */
	set(name: string, secret: string): boolean;
	get(name: string): string | null;
	delete(name: string): void;
}

const keyringStore: SecretStore = {
	set(name, secret) {
		const mod = loadKeyring();
		if (!mod) return false;
		try {
			new mod.Entry(SERVICE, name).setPassword(secret);
			return true;
		} catch {
			return false;
		}
	},
	get(name) {
		const mod = loadKeyring();
		if (!mod) return null;
		try {
			return new mod.Entry(SERVICE, name).getPassword();
		} catch {
			return null;
		}
	},
	delete(name) {
		const mod = loadKeyring();
		if (!mod) return;
		try {
			new mod.Entry(SERVICE, name).deletePassword();
		} catch {
			// nothing to delete (or keystore unavailable) — treat as already gone
		}
	},
};

let cachedMachineId: string | undefined;

/** A stable per-machine identifier, so ciphertext can't be decrypted off-box. */
function machineId(env: NodeJS.ProcessEnv): string {
	const override = env.WHOP_CLI_MACHINE_ID?.trim();
	if (override) return override;
	if (cachedMachineId !== undefined) return cachedMachineId;
	cachedMachineId = detectMachineId();
	return cachedMachineId;
}

function detectMachineId(): string {
	try {
		if (process.platform === "darwin") {
			const out = execFileSync(
				"ioreg",
				["-rd1", "-c", "IOPlatformExpertDevice"],
				{ encoding: "utf8" },
			);
			return /"IOPlatformUUID"\s*=\s*"([^"]+)"/.exec(out)?.[1] ?? "";
		}
		if (process.platform === "linux") {
			for (const file of ["/etc/machine-id", "/var/lib/dbus/machine-id"]) {
				try {
					const id = fs.readFileSync(file, "utf8").trim();
					if (id) return id;
				} catch {
					// try the next candidate
				}
			}
			return "";
		}
		if (process.platform === "win32") {
			const out = execFileSync(
				"reg",
				[
					"query",
					"HKLM\\SOFTWARE\\Microsoft\\Cryptography",
					"/v",
					"MachineGuid",
				],
				{ encoding: "utf8" },
			);
			return /MachineGuid\s+REG_SZ\s+([\w-]+)/.exec(out)?.[1] ?? "";
		}
	} catch {
		// no machine id — deriveKey falls back to a constant (still salted per value)
	}
	return "";
}

function deriveKey(machine: string, salt: Buffer): Buffer {
	// hkdf needs non-empty ikm; use a constant stand-in when there's no machine id
	const ikm = machine || "whop-cli/no-machine-id";
	return Buffer.from(hkdfSync("sha256", ikm, salt, KEY_PEPPER, 32));
}

function encryptValue(plaintext: string, machine: string): string {
	const salt = randomBytes(16);
	const iv = randomBytes(12);
	const cipher = createCipheriv("aes-256-gcm", deriveKey(machine, salt), iv);
	const ciphertext = Buffer.concat([
		cipher.update(Buffer.from(plaintext, "utf8")),
		cipher.final(),
	]);
	const payload = Buffer.concat([iv, ciphertext, cipher.getAuthTag()]);
	// base64 never contains "-", so the salt prefix splits unambiguously.
	return `${salt.toString("base64")}-${payload.toString("base64")}`;
}

function decryptValue(stored: string, machine: string): string | null {
	const dash = stored.indexOf("-");
	if (dash < 0) return null;
	const salt = Buffer.from(stored.slice(0, dash), "base64");
	const payload = Buffer.from(stored.slice(dash + 1), "base64");
	if (payload.length < 12 + 16) return null;
	const iv = payload.subarray(0, 12);
	const tag = payload.subarray(payload.length - 16);
	const ciphertext = payload.subarray(12, payload.length - 16);
	try {
		const decipher = createDecipheriv(
			"aes-256-gcm",
			deriveKey(machine, salt),
			iv,
		);
		decipher.setAuthTag(tag);
		return Buffer.concat([
			decipher.update(ciphertext),
			decipher.final(),
		]).toString("utf8");
	} catch {
		// wrong machine id, tampered ciphertext, or corrupt salt — treat as absent
		return null;
	}
}

function credentialsPath(env: NodeJS.ProcessEnv): string {
	return path.join(configDir(env), "credentials.json");
}

function sanitizeStringMap(value: unknown): Record<string, string> {
	if (value == null || typeof value !== "object" || Array.isArray(value)) {
		return {};
	}
	const out: Record<string, string> = {};
	for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
		if (typeof val === "string") out[key] = val;
	}
	return out;
}

interface StoredFile {
	/** A version-1 plaintext file whose values must be encrypted on next write. */
	legacy: boolean;
	/** Raw on-disk values: ciphertext for v3, plaintext for legacy v1. */
	secrets: Record<string, string>;
}

// Raw on-disk map, undecrypted, so a rewrite doesn't drop entries we can't read.
function readStoredFile(env: NodeJS.ProcessEnv): StoredFile {
	let raw: string;
	try {
		raw = fs.readFileSync(credentialsPath(env), "utf8");
	} catch {
		return { legacy: false, secrets: {} };
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		// corrupt file — treat as empty rather than bricking the CLI
		return { legacy: false, secrets: {} };
	}
	if (parsed == null || typeof parsed !== "object") {
		return { legacy: false, secrets: {} };
	}
	const obj = parsed as { version?: unknown; secrets?: unknown };
	return { legacy: obj.version !== 3, secrets: sanitizeStringMap(obj.secrets) };
}

// Persist as v3: keep existing ciphertext verbatim (so unreadable entries survive a write); encrypt a legacy v1 file's plaintext.
function toCiphertextMap(
	stored: StoredFile,
	env: NodeJS.ProcessEnv,
): Record<string, string> {
	if (!stored.legacy) return { ...stored.secrets };
	const machine = machineId(env);
	const encrypted: Record<string, string> = {};
	for (const [name, value] of Object.entries(stored.secrets)) {
		encrypted[name] = encryptValue(value, machine);
	}
	return encrypted;
}

function writeStoredFile(
	secrets: Record<string, string>,
	env: NodeJS.ProcessEnv,
): void {
	const dir = configDir(env);
	fs.mkdirSync(dir, { recursive: true, mode: 0o700 });
	const file = credentialsPath(env);
	const tmp = `${file}.${process.pid}.tmp`;
	fs.writeFileSync(
		tmp,
		`${JSON.stringify({ version: 3, secrets }, null, 2)}\n`,
		{ mode: 0o600 },
	);
	fs.renameSync(tmp, file);
	try {
		fs.chmodSync(file, 0o600);
	} catch {
		// best effort — chmod is a no-op on some platforms (e.g. Windows)
	}
}

function fileStore(env: NodeJS.ProcessEnv): SecretStore {
	return {
		set(name, secret) {
			const secrets = toCiphertextMap(readStoredFile(env), env);
			secrets[name] = encryptValue(secret, machineId(env));
			writeStoredFile(secrets, env);
			return true;
		},
		get(name) {
			const stored = readStoredFile(env);
			const value = stored.secrets[name];
			if (value == null) return null;
			return stored.legacy ? value : decryptValue(value, machineId(env));
		},
		delete(name) {
			// Rewrite even when the entry doesn't parse — a corrupt/legacy file still holds secret bytes to clear.
			if (!fs.existsSync(credentialsPath(env))) return;
			const secrets = toCiphertextMap(readStoredFile(env), env);
			delete secrets[name];
			writeStoredFile(secrets, env);
		},
	};
}

type StoreKind = "keyring" | "file";

interface StorePolicy {
	platform: NodeJS.Platform;
	isStandaloneBinary: boolean;
	isNpx: boolean;
	forced?: string;
}

// Ordered stores to try; pure over its inputs so the policy is unit-testable.
export function resolveStoreKinds(policy: StorePolicy): StoreKind[] {
	if (policy.forced === "keyring") return ["keyring"];
	if (policy.forced === "file") return ["file"];
	// Standalone binaries self-update (signature changes → keychain re-prompts) and can't load the addon.
	if (policy.isStandaloneBinary) return ["file"];
	// macOS + npx (agent path): keychain re-prompts on each ephemeral copy.
	if (policy.platform === "darwin" && policy.isNpx) return ["file"];
	return ["keyring", "file"];
}

function storeFor(kind: StoreKind, env: NodeJS.ProcessEnv): SecretStore {
	return kind === "keyring" ? keyringStore : fileStore(env);
}

function storeKindsFor(env: NodeJS.ProcessEnv): StoreKind[] {
	return resolveStoreKinds({
		platform: process.platform,
		isStandaloneBinary: buildTarget != null,
		isNpx: isNpx(),
		forced: env.WHOP_CLI_SECRET_STORE?.trim(),
	});
}

// Clear both stores so a prior install's copy can't shadow a write or survive logout; honor a forced store (keeps forced-file off the keychain).
function cleanupKindsFor(env: NodeJS.ProcessEnv): StoreKind[] {
	const forced = env.WHOP_CLI_SECRET_STORE?.trim();
	if (forced === "file") return ["file"];
	if (forced === "keyring") return ["keyring"];
	return ["keyring", "file"];
}

// Per-process read cache; a run is short-lived and this is the only writer, so no staleness.
const secretCache = new Map<string, string | null>();

function cacheKey(name: string, env: NodeJS.ProcessEnv): string {
	return `${storeKindsFor(env)[0]} ${configDir(env)} ${name}`;
}

export function setSecret(
	name: string,
	secret: string,
	env: NodeJS.ProcessEnv = process.env,
): void {
	const winner = storeKindsFor(env).find((kind) =>
		storeFor(kind, env).set(name, secret),
	);
	if (!winner) {
		throw new Error(
			"WHOP_CLI_SECRET_STORE=keyring but the OS keystore is unavailable",
		);
	}
	for (const kind of cleanupKindsFor(env)) {
		if (kind !== winner) storeFor(kind, env).delete(name);
	}
	secretCache.set(cacheKey(name, env), secret);
}

export function getSecret(
	name: string,
	env: NodeJS.ProcessEnv = process.env,
): string | null {
	const key = cacheKey(name, env);
	const cached = secretCache.get(key);
	if (cached !== undefined) return cached;
	let result: string | null = null;
	for (const kind of storeKindsFor(env)) {
		const secret = storeFor(kind, env).get(name);
		if (secret != null) {
			result = secret;
			break;
		}
	}
	secretCache.set(key, result);
	return result;
}

export function deleteSecret(
	name: string,
	env: NodeJS.ProcessEnv = process.env,
): void {
	for (const kind of cleanupKindsFor(env)) {
		storeFor(kind, env).delete(name);
	}
	secretCache.set(cacheKey(name, env), null);
}
